<template>
	<div
		class="form__field"
		:class="{
			readonly,
			'form__field--has-error': error || highlightError,
			'form__field--without-label': withoutLabel,
			'form__field--centered-text': centeredText,
			'form__field--menu-style': menuStyle
		}"
	>
		<div class="form__field__input-wrapper">
			<input
				v-mask="mask"
				class="form__field__input"
				:type="type"
				ref="inputRef"
				:required="required"
				:autocomplete="autocomplete"
				spellcheck="false"
				:value="currentValue"
				:maxlength="maxLength"
				:readonly="readonly"
				:disabled="disabled"
				:placeholder="inputPlaceholder"
				:id="inputId"
				:inputmode="inputmode"
				:key="key"
				@input.prevent="handleInput"
				@change.prevent="onChange"
				@blur="onBlur"
				@focus="handleFocus"
				@keyup.enter="onEnter"
				@keyup="onKeyUp"
				@keydown="onKeyDown"
			/>
			<label
				v-if="label && !withoutLabel"
				:for="inputId"
				class="form__field__label"
			>
				{{ label }}
			</label>
			<div
				v-if="currentValue && cleanable"
				class="form__field__clean-icon"
				tabindex="-1"
				@click.stop="cleanValue"
			>
				<img :src="closeIcon" alt="" />
			</div>
			<div v-if="error" class="form__field__error">
				<img
					v-if="errorIconVisible"
					class="form__field__error__icon"
					:src="errorIcon"
					alt="error icon"
				/>
				<span>{{ error }}</span>
			</div>
			<div
				v-else-if="infoMessage"
				class="form__field__info"
				:class="{ 'form__field__info--success': isInfoMessageSuccess }"
			>
				{{ infoMessage }}
			</div>
			<span class="input-counter form__field__counter" v-if="showCounter">
				{{ currentValue ? currentValue.length : 0 }}/{{ maxLength }}
			</span>
		</div>
	</div>
</template>

<script setup>
// TODO: Rename component
// Vue
import { computed, ref } from 'vue'
import { useInput } from '@/components/Ticketing/Controls/script/useInput'
// Utils
import { v4 as uuidv4 } from 'uuid'
// Icons
import ErrorIcon from '@/assets/images/ic_info_error.svg'
import CloseIcon from '@/assets/images/ic_cancel_grey.svg'
import { createVuexHelpers } from 'vue2-helpers'

// Props
const props = defineProps({
	value: {
		type: String,
		default: ''
	},

	withoutLabel: {
		type: Boolean,
		default: false
	},

	required: {
		type: Boolean,
		default: false
	},

	centeredText: {
		type: Boolean,
		default: false
	},

	placeholder: {
		type: String,
		default: ''
	},

	type: {
		type: String,
		default: 'text'
	},

	maxLength: {
		type: Number,
		default: 30
	},

	autoSelectText: {
		type: Boolean,
		default: false
	},

	readonly: {
		type: Boolean,
		default: false
	},

	disabled: {
		type: Boolean,
		default: false
	},

	error: {
		type: String,
		default: ''
	},

	debounced: {
		type: Boolean,
		default: false
	},

	label: {
		type: String,
		default: ''
	},

	highlightError: {
		type: Boolean,
		default: false
	},

	infoMessage: {
		type: String,
		default: ''
	},

	autocomplete: {
		type: String,
		default: 'off'
	},

	inputmode: {
		type: String,
		default: ''
	},

	mask: {
		type: String,
		default: ''
	},

	cleanable: {
		type: Boolean,
		default: false
	},

	menuStyle: {
		type: Boolean,
		default: false
	},

	showCounter: {
		type: Boolean,
		default: false
	},

	errorIconVisible: {
		type: Boolean,
		default: true
	}
})

const { maxLength, label, readonly } = props

// Store
const { useState } = createVuexHelpers()
const { isMacOrIOS } = useState('ui', ['isMacOrIOS'])

// Emits
const emit = defineEmits(['input', 'enter', 'change', 'key-up', 'key-down'])

// Refs
const currentValue = ref('')
const inputRef = ref(null)
const inputId = ref(uuidv4())
const key = ref(uuidv4())

// Computed
const inputPlaceholder = computed(() =>
	!props.withoutLabel ? props.label : props.placeholder
)
const errorIcon = computed(() => ErrorIcon)
const closeIcon = computed(() => CloseIcon)

// Methods
const focusInput = () => {
	if (inputRef) inputRef.value.focus()
}

const onKeyUp = e => {
	emit('key-up', e)
}

const onKeyDown = e => {
	emit('key-down', e)
}

const updateComponent = () => {
	inputId.value = uuidv4()
}

const cleanValue = () => {
	currentValue.value = ''
	emit('clean')
	// inputRef.value.dispatchEvent(new Event('input', { bubbles: true }))
	if (isMacOrIOS.value) {
		key.value = uuidv4()
	} else {
		focusInput()
	}
}

// Composable
const {
	handleFocus,
	onBlur,
	onChange,
	onEnter,
	handleInput,
	isInfoMessageSuccess
} = useInput(props, currentValue, emit)
// Expose
defineExpose({
	focusInput,
	updateComponent
})
</script>

<style lang="scss" scoped>
@import 'styles/_input.scss';
</style>
